/**
 * THE FILTER, WIRED TO THE ADDRESS BAR.
 *
 * One hook owns the whole query: it seeds from the URL, writes every change back
 * to it, adopts a change that came from OUTSIDE it (the back button, a redirect,
 * a pasted link), and re-seeds the server-owned defaults exactly once when
 * `meta` lands.
 *
 * ── PUSH VERSUS REPLACE ──
 *
 * A discrete action — a tab change, a preset, a drill, loading a saved view —
 * PUSHES, so the back button steps through states a reader would recognise.
 * Scrubbing — typing in the search box, dragging a brush — REPLACES, so a
 * three-word search does not leave three entries in the history and take three
 * presses of Back to escape.
 *
 * ── WHY THE WRITE PATH DOES NOT USE `useSearchParams` ──
 *
 * See the long note on `nextSearch` in filter-url.ts. Short version: React's copy
 * of the query string lags `history.pushState` by one commit under
 * v7_startTransition, so composing a write from it drops whatever the previous
 * control just set. Every write here composes from `window.location.search`.
 * Reading is the opposite case and DOES use the router's location, because
 * adopting an external change is exactly the moment React's copy is the
 * authority.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardFilter, FilterMeta, roleDefaults } from './filter-model';
import { FilterAction, filterReducer, seedFromMeta } from './filter-reducer';
import { decode, hasLegacyWindow, liveParams, nextSearch } from './filter-url';

export interface DispatchOptions {
  /** Replace the history entry instead of pushing one. Default: push. */
  replace?: boolean;
}

export interface DashboardFilterApi {
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: DispatchOptions) => void;
  /** The query string this filter serialises to, for Save and for Copy link. */
  search: string;
}

/** Actions that are a continuous gesture rather than a discrete choice. */
const REPLACING: FilterAction['type'][] = ['patch', 'setCustomRange'];

export const useDashboardFilter = (
  meta?: FilterMeta,
  /** The signed-in role, for the role-tailored defaults (QA-07). Optional — an
   *  absent role simply gets the owner's defaults, which is what every caller
   *  got before this existed. */
  role?: string | null
): DashboardFilterApi => {
  const location = useLocation();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<DashboardFilter>(() => {
    /* ROLE DEFAULTS APPLY TO AN EMPTY ADDRESS BAR AND NOTHING ELSE (QA-07).
     *
     * `decode` fills every absent key from DEFAULT_FILTER, so it cannot tell
     * "the reader asked for the 30-day window" from "the reader asked for
     * nothing". That distinction is the whole feature: a link somebody pasted
     * has to mean the same thing for the manager who opens it as for the owner
     * who sent it, so ANY filter in the URL disables the role default outright.
     *
     * Deliberately not "no params at all": the screen's own tab lives in the
     * query string too, and a reader who navigated to ?tab=records has made a
     * choice even though they set no filter. `liveParams().size` would treat
     * that as empty; asking whether the string is empty does not. */
    const params = liveParams();
    const untouched = params.toString() === '';

    return decode(params, meta, untouched ? roleDefaults(role) : undefined);
  });

  /* A mirror of the state, so `dispatch` can compute the next filter without
     doing it inside a setState updater. An updater that navigates is a side
     effect in what React is entitled to treat as a pure function, and StrictMode
     calls it twice — which would push two history entries per action. */
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const metaRef = useRef<FilterMeta | undefined>(meta);
  metaRef.current = meta;

  /* The last search string THIS hook wrote, so the adopt-external-change effect
     below can tell "the reader pressed Back" from "we just navigated". */
  const writtenRef = useRef<string>('');

  /* Which fields the reader has actually chosen. `seedFromMeta` must not
     overwrite a value somebody set before the descriptor arrived — which is a
     real race on a slow connection, where the range chips are pressable from the
     first paint. */
  const touchedRef = useRef<Set<keyof DashboardFilter>>(new Set());

  const seededRef = useRef(false);

  /* ── an old shared link: rewrite it once, in day keys ──
     decode() has already read its instants as days on this device's clock. The
     address bar is brought into line straight away (REPLACE — the reader did
     not navigate), so the link they copy from here means the same days to
     whoever opens it next, in whatever zone. */
  useEffect(() => {
    if (!hasLegacyWindow(liveParams())) return;
    const search = nextSearch(filterRef.current);
    writtenRef.current = search;
    navigate({ search: search ? `?${search}` : '' }, { replace: true });
    // Once, on mount: a legacy window can only arrive in the address the screen
    // was opened with or one adopted from outside, which decode() handles.
    // eslint-disable-next-line
  }, []);

  /* ── seed the server-owned defaults, once ── */
  useEffect(() => {
    if (!meta || seededRef.current) return;
    seededRef.current = true;
    const next = seedFromMeta(filterRef.current, meta, touchedRef.current);
    if (JSON.stringify(next) === JSON.stringify(filterRef.current)) return;
    filterRef.current = next;
    setFilter(next);
    const search = nextSearch(next);
    writtenRef.current = search;
    // REPLACE, never push. Applying a default the reader did not choose must not
    // put an entry in their history for them to press Back through.
    navigate({ search: search ? `?${search}` : '' }, { replace: true });
  }, [meta, navigate]);

  /* ── adopt a change that came from outside this hook ──
     Back/forward, a redirect from a legacy route, or a link opened in place. */
  useEffect(() => {
    const incoming = location.search.replace(/^\?/, '');
    if (incoming === writtenRef.current) return;
    writtenRef.current = incoming;
    const next = decode(new URLSearchParams(incoming), metaRef.current);
    filterRef.current = next;
    setFilter(next);
  }, [location.search]);

  const dispatch = useCallback(
    (action: FilterAction, opts?: DispatchOptions) => {
      const current = filterRef.current;
      const next = filterReducer(current, action, metaRef.current);

      // Nothing changed — do not touch the history for a no-op press.
      if (JSON.stringify(next) === JSON.stringify(current)) return;

      for (const key of Object.keys(next) as (keyof DashboardFilter)[]) {
        if (next[key] !== current[key]) touchedRef.current.add(key);
      }

      filterRef.current = next;
      setFilter(next);

      const search = nextSearch(next);
      writtenRef.current = search;
      const replace =
        opts?.replace ?? REPLACING.includes(action.type);
      navigate({ search: search ? `?${search}` : '' }, { replace });
    },
    [navigate]
  );

  return { filter, dispatch, search: nextSearch(filter) };
};
